import CheckIcon from '@heroicons/react/20/solid/CheckIcon.js';
import React, { useContext } from 'react';
import { useObjectRef } from 'react-aria';
import { CheckboxButton, CheckboxField } from 'react-aria-components';

import { tv } from '../../tv';
import { Size } from '../../types';
import { FieldMessage, resolveFieldState, useFieldMessageIds } from '../field/field-shell';
import { RequiredMark } from '../field/required-mark';
import { CheckboxGroupStyleContext } from './group-context';

/**
 * ComplyKit Checkbox: React Aria Components (CheckboxField + CheckboxButton)
 * + Tailwind v4 + tailwind-variants.
 * Props are fully abstracted from the underlying library.
 *
 * The label text renders INSIDE the CheckboxButton (the pressed <label>
 * wrapping the input), so clicking the box or the text toggles and the text
 * is the accessible name. Note: react-aria deliberately ignores presses it
 * classifies as virtual (zero-size pointer events, which is what most
 * automation tools dispatch); real pointers and keyboard work.
 * The unchecked box is transparent so the control composes on any surface
 * (the norm across Radix, shadcn, Material, and Chakra).
 *
 * Its message line is a field's (field.md): a description under the label,
 * inset to the label's start, and an error that replaces it.
 */
interface CheckboxCommonProps {
  /**
   * Help under the label: what selecting it does, or why it is unavailable.
   * Inline content only. Hidden while `error` shows.
   */
  description?: React.ReactNode;
  /**
   * The error message. The checkbox is invalid exactly when this is
   * non-empty. Say what to do: "Accept the terms to continue".
   */
  error?: string;
  /** Whether the checkbox is checked (controlled). */
  isSelected?: boolean;
  /** Whether the checkbox is checked on mount (uncontrolled). */
  defaultSelected?: boolean;
  /** Whether the checkbox is in an indeterminate state (partial selection). */
  isIndeterminate?: boolean;
  /** Called with the new checked state on every change. */
  onChange?: (isSelected: boolean) => void;
  /** Called when the input loses focus. */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** Called when the input receives focus. */
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** Whether the input takes focus on mount. */
  autoFocus?: boolean;
  /**
   * Washed out and skipped by Tab. Prefer `isReadOnly` whenever the value
   * still matters to the reader.
   */
  isDisabled?: boolean;
  /**
   * Focusable but not changeable, the mark in neutral. Say why in
   * `description`. Also the state for an in-flight mutation: disabling a
   * focused control drops its focus.
   */
  isReadOnly?: boolean;
  /** Marks the label with an asterisk and exposes `aria-required`. */
  isRequired?: boolean;
  /** The value this checkbox contributes inside a CheckboxGroup. */
  value?: string;
  /** The form field name. */
  name?: string;
  /** The id of the input element. */
  id?: string;
  /**
   * Visual emphasis of the selected state (the unselected box is identical
   * across variants). From the canonical ComplyKit variant vocabulary.
   * Defaults to the enclosing CheckboxGroup's variant when present.
   */
  variant?: 'solid' | 'soft' | 'outline';
  /**
   * Recolours the control onto a semantic scale: `critical` for a choice
   * that destroys, `ai` for one the agent made. Absent means the brand
   * accent.
   */
  tone?: 'critical' | 'ai';
  /** Size of the checkbox. Defaults to the enclosing CheckboxGroup's size. */
  size?: Size;
}

/* A checkbox must have a name, held by the type as Radio's is: the visible
 * label, an aria-label, or the id of the element that names it. */
interface CheckboxLabelledProps extends CheckboxCommonProps {
  /** The visible label; renders inside the pressed label element, so it is
   * also the accessible name. */
  children: React.ReactNode;
  /** Override for the rare checkbox whose visible label needs rephrasing for
   * screen readers. */
  'aria-label'?: string;
  'aria-labelledby'?: never;
}

interface CheckboxUnlabelledProps extends CheckboxCommonProps {
  children?: never;
  /** The accessible name of a checkbox with no visible label (a table row). */
  'aria-label': string;
  'aria-labelledby'?: never;
}

interface CheckboxLabelledByProps extends CheckboxCommonProps {
  children?: never;
  'aria-label'?: never;
  /** The id of the visible element that names the checkbox (a question's text). */
  'aria-labelledby': string;
}

export type CheckboxProps =
  | CheckboxLabelledProps
  | CheckboxUnlabelledProps
  | CheckboxLabelledByProps;

export const checkboxStyles = tv({
  slots: {
    root: ['ck:group ck:font-sans ck:text-text-default', 'ck:select-none'],
    // The button is a group too: react-aria places data-focus-visible (and
    // press states) on the pressed label, not on the field root. It washes
    // out alone when disabled, so the message stays legible: the reason a
    // checkbox is unavailable is its description.
    button: [
      'ck:group ck:flex ck:items-center ck:gap-2',
      'ck:group-disabled:cursor-not-allowed ck:group-disabled:opacity-50',
    ],
    // Inset past the box and its gap, so the message starts under the label.
    message: 'ck:mt-1',
    // No transition: the check appears instantly, and a fill that fades
    // under an instant glyph reads as flicker, so selection snaps.
    box: [
      'ck:flex ck:shrink-0 ck:items-center ck:justify-center',
      'ck:rounded-2 ck:border ck:border-border-default ck:bg-transparent',
      'ck:group-focus-visible:focus-ring',
      // Stacked, so the edge outranks a selected variant's: a checked box that is invalid stays red.
      'ck:group-invalid:border-error-border ck:group-invalid:group-selected:border-error-border',
      'ck:group-invalid:group-indeterminate:border-error-border',
    ],
    icon: '',
    // The indeterminate mark is CSS-drawn: heroicons' MinusIcon sits left of
    // its viewBox center, so a flex-centered bar is the only exact glyph.
    bar: 'ck:rounded-6 ck:bg-current',
  },
  variants: {
    /* The boxes are the Chakra ramp the product rendered (12/16/20); the
     * labels sit on the field ramp (12/14/16, typography.md), so a checkbox
     * beside a 32px field reads at the field's size. */
    size: {
      sm: {
        root: 'ck:text-xs',
        box: 'ck:size-3',
        icon: 'ck:size-2',
        bar: 'ck:h-0.5 ck:w-1.5',
        message: 'ck:choice-inset-sm',
      },
      md: {
        root: 'ck:text-sm',
        box: 'ck:size-4',
        icon: 'ck:size-3',
        bar: 'ck:h-0.5 ck:w-2',
        message: 'ck:choice-inset-md',
      },
      lg: {
        root: 'ck:text-base',
        box: 'ck:size-5',
        icon: 'ck:size-4',
        bar: 'ck:h-0.5 ck:w-2.5',
        message: 'ck:choice-inset-lg',
      },
    },
    /* Read-only keeps the mark and drops every cue that invites a press:
     * no hover, no hand, and the tone turns neutral (field.md). */
    readOnly: {
      false: { button: 'ck:cursor-pointer', box: 'ck:group-hover:border-border-active' },
      true: { button: 'ck:cursor-default' },
    },
    variant: {
      solid: {
        box: [
          'ck:text-tone-contrast',
          'ck:group-selected:border-transparent ck:group-selected:bg-fill',
          'ck:group-indeterminate:border-transparent ck:group-indeterminate:bg-fill',
        ],
      },
      soft: {
        box: [
          'ck:text-tone-text',
          'ck:group-selected:border-tone-a-7 ck:group-selected:bg-tone-a-3',
          'ck:group-indeterminate:border-tone-a-7 ck:group-indeterminate:bg-tone-a-3',
        ],
      },
      outline: {
        box: [
          'ck:text-tone-text',
          'ck:group-selected:border-tone-a-8',
          'ck:group-indeterminate:border-tone-a-8',
        ],
      },
    },
  },
});

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  {
    children,
    description,
    error: errorProp,
    isRequired: requiredProp,
    isDisabled: disabledProp,
    isReadOnly: readOnlyProp,
    onBlur,
    onFocus,
    size,
    variant,
    tone,
    ...props
  },
  ref,
) {
  const inputRef = useObjectRef(ref);
  const group = useContext(CheckboxGroupStyleContext);
  const { error, isRequired, isDisabled, isReadOnly, isInvalid } = resolveFieldState({
    error: errorProp,
    isRequired: requiredProp,
    isDisabled: disabledProp,
    isReadOnly: readOnlyProp,
  });
  const readOnly = isReadOnly || !!group.isReadOnly;
  const { root, button, box, icon, bar, message } = checkboxStyles({
    size: size ?? group.size ?? 'md',
    variant: variant ?? group.variant ?? 'solid',
    readOnly,
  });
  const { descriptionId, errorId, describedBy } = useFieldMessageIds(description, error);

  return (
    <CheckboxField
      {...props}
      inputRef={inputRef}
      onBlur={onBlur as ((e: React.FocusEvent<Element>) => void) | undefined}
      onFocus={onFocus as ((e: React.FocusEvent<Element>) => void) | undefined}
      isRequired={isRequired}
      isDisabled={isDisabled}
      isReadOnly={isReadOnly}
      isInvalid={isInvalid}
      validationBehavior="aria"
      aria-describedby={describedBy}
      className={root()}
      data-component="checkbox"
      data-tone={readOnly ? 'neutral' : (tone ?? 'accent')}
    >
      <CheckboxButton className={button()}>
        {({ isSelected, isIndeterminate }) => (
          <>
            <span className={box()} aria-hidden="true">
              {isIndeterminate ? (
                <span className={bar()} />
              ) : isSelected ? (
                <CheckIcon className={icon()} />
              ) : null}
            </span>
            {isRequired && !group.inGroup && children ? (
              <span>
                {children}
                <RequiredMark />
              </span>
            ) : (
              children
            )}
          </>
        )}
      </CheckboxButton>
      {description || error ? (
        <div className={message()}>
          <FieldMessage
            description={description}
            error={error}
            descriptionId={descriptionId}
            errorId={errorId}
          />
        </div>
      ) : null}
    </CheckboxField>
  );
});
